import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import ko from '../../language/Ko-kr.json';
import en from '../../language/En-eng.json';
import { ResponsiveShell } from '../../components/ResponsiveShell';

const isKorean = true; // TODO: 실제 언어 설정 연동
const t = isKorean ? ko : en;

/* ─── Global & Animations ────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background: #E8E0C8;
    color: #5C5040;
    -webkit-font-smoothing: antialiased;
  }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0.5; }
  to { transform: translateY(0); opacity: 1; }
`;

/* ─── Stagger List Animation ────────────────────────────────────────────── */
const listContainer = {
  animate: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
const listItem = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0, transition: { type: 'spring' as const, stiffness: 320, damping: 26 } },
};

/* ─── Phone Shell ────────────────────────────────────────────────────────── */

/* ─── Nav Bar ────────────────────────────────────────────────────────────── */
const PhoneNavBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 12px;
  flex-shrink: 0;
  background: rgba(249, 246, 232, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1.5px solid #EDE9D5;
  z-index: 10;
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #EDE9D5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  color: #5C5040;
  &:hover { background: #E0D8C0; }
`;

const NavTitle = styled.h2`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #3D3020;
  flex: 1;
  text-align: center;
  margin-right: 36px; 
`;

/* ─── Body ───────────────────────────────────────────────────────────────── */
const PhoneBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 24px;
  gap: 24px;
`;

/* ─── Search ─────────────────────────────────────────────────────────────── */
const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1.5px solid #D8D0B8;
  border-radius: 16px;
  padding: 14px 16px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  background: #FFF;
  outline: none;
  &:focus { border-color: #E07040; }
`;

const SearchResultCard = styled(motion.div)`
  background: #FFF;
  border: 1.5px solid #D8D0B8;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 12px rgba(90, 60, 30, 0.05);
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #E8E0C8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B0A080;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.div`
  font-weight: 700;
  color: #3D3020;
  font-size: 15px;
`;

const UserStats = styled.div`
  font-size: 12px;
  color: #8C8070;
`;

const FollowBtn = styled.button<{ $isFollowing?: boolean }>`
  background: ${p => p.$isFollowing ? '#EDE9D5' : '#E07040'};
  color: ${p => p.$isFollowing ? '#5C5040' : '#FFF'};
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

/* ─── List ───────────────────────────────────────────────────────────────── */
const ListSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 24px;
  border-bottom: 1.5px solid #D8D0B8;
  padding-bottom: 8px;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  font-family: 'Jua', sans-serif;
  font-size: 16px;
  color: ${p => p.$active ? '#E07040' : '#A09070'};
  cursor: pointer;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -9.5px;
    left: 0;
    width: 100%;
    height: 3px;
    background: #E07040;
    border-radius: 3px 3px 0 0;
    display: ${p => p.$active ? 'block' : 'none'};
  }
`;

const FriendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FriendItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid #E8E0C8;
  padding: 12px;
  border-radius: 12px;
`;

/* ─── Component ──────────────────────────────────────────────────────────── */
type User = {
  user_email: string;
  user_name: string;
  user_alias?: string;
  followers_count?: number;
};

const FriendAdd: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchedUsers, setSearchedUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  const [activeTab, setActiveTab] = useState<'following' | 'followers'>('following');
  const [following, setFollowing] = useState<User[]>([]);
  const [followers, setFollowers] = useState<User[]>([]);

  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  const [popup, setPopup] = useState<{ title: string; msg: string } | null>(null);

  // Load followers / following
  const fetchFriends = async () => {
    const token = getToken();
    if (!token) return;

    try {
      // Fetch Current User
      const resMe = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resMe.ok) {
        const data = await resMe.json();
        setCurrentUser(data);
      }

      // Fetch Following
      const resFollowing = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/following`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resFollowing.ok) {
        const data = await resFollowing.json();
        setFollowing(Array.isArray(data) ? data : []);
      }

      // Fetch Followers
      const resFollowers = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/followers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resFollowers.ok) {
        const data = await resFollowers.json();
        setFollowers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchFriends();
  }, []);

  // Handle Search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchedUsers([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      const token = getToken();
      if (!token) return;

      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/search?q=${encodeURIComponent(searchQuery)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSearchedUsers(Array.isArray(data) ? data : []);
        } else {
          setSearchedUsers([]);
        }
      } catch (err) {
        console.error(err);
        setSearchedUsers([]);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Follow / Unfollow
  const handleFollowToggle = async (alias: string | undefined, isFollowing: boolean) => {
    if (!alias) return;
    const token = getToken();
    if (!token) {
      setPopup({ title: '친구 추가', msg: t.friends.loginRequired });
      return;
    }

    try {
      const url = `/user/follow/${encodeURIComponent(alias)}`;
      const response = await fetch(url, {
        method: isFollowing ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchFriends();
      } else {
        setPopup({ title: '친구 추가', msg: t.friends.reqFailed });
      }
    } catch (err) {
      console.error(err);
      setPopup({ title: '친구 추가', msg: t.friends.serverError });
    }
  };

  // 친구 목록에 있는지 확인
  const isUserFollowing = (alias: string | undefined) => {
    if (!alias) return false;
    return following.some(f => f.user_alias === alias);
  };

  return (
    <>
      <GlobalStyle />
      <ResponsiveShell>
        <PhoneNavBar>
          <BackBtn onClick={() => { window.location.href = '/'; }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </BackBtn>
          <NavTitle>{t.friends.title}</NavTitle>
        </PhoneNavBar>

        <PhoneBody>
          <SearchContainer>
            <SearchInput 
              placeholder={t.friends.searchPlaceholder} 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            
            <motion.div variants={listContainer} initial="initial" animate="animate">
              {searchedUsers.map(user => (
                <SearchResultCard key={user.user_email} variants={listItem}>
                <ProfileInfo>
                  <Avatar>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </Avatar>
                  <UserDetails>
                    <UserName>{user.user_name}</UserName>
                    <UserStats>{user.user_alias}</UserStats>
                  </UserDetails>
                </ProfileInfo>
                {currentUser?.user_alias !== user.user_alias && (
                  <FollowBtn 
                    $isFollowing={isUserFollowing(user.user_alias)}
                    onClick={() => handleFollowToggle(user.user_alias, isUserFollowing(user.user_alias))}
                  >
                    {isUserFollowing(user.user_alias) 
                      ? t.friends.unfollow
                      : t.friends.follow}
                  </FollowBtn>
                )}
                </SearchResultCard>
              ))}
            </motion.div>
          </SearchContainer>

          <ListSection>
            <Tabs>
              <Tab $active={activeTab === 'following'} onClick={() => setActiveTab('following')}>
                {t.friends.following} {following.length}
              </Tab>
              <Tab $active={activeTab === 'followers'} onClick={() => setActiveTab('followers')}>
                {t.friends.followers} {followers.length}
              </Tab>
            </Tabs>

            <FriendList>
              {activeTab === 'following' && (
                <motion.div variants={listContainer} initial="initial" animate="animate">
                  {following.map(user => (
                    <FriendItem key={user.user_email} variants={listItem}>
                  <ProfileInfo>
                    <Avatar>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </Avatar>
                    <UserDetails>
                      <UserName>{user.user_name}</UserName>
                      <UserStats>{user.user_alias || user.user_email}</UserStats>
                    </UserDetails>
                  </ProfileInfo>
                  <FollowBtn 
                    $isFollowing={true}
                    onClick={() => handleFollowToggle(user.user_alias, true)}
                  >
                    {t.friends.unfollow}
                  </FollowBtn>
                    </FriendItem>
                  ))}
                </motion.div>
              )}

              {activeTab === 'following' && following.length === 0 && (
                <UserStats style={{ textAlign: 'center', padding: '20px' }}>
                  {t.friends.noFollowing}
                </UserStats>
              )}

              {activeTab === 'followers' && (
                <motion.div variants={listContainer} initial="initial" animate="animate">
                  {followers.map(user => (
                    <FriendItem key={user.user_email} variants={listItem}>
                  <ProfileInfo>
                    <Avatar>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </Avatar>
                    <UserDetails>
                      <UserName>{user.user_name}</UserName>
                      <UserStats>{user.user_alias || user.user_email}</UserStats>
                    </UserDetails>
                  </ProfileInfo>
                  {!isUserFollowing(user.user_alias) && (
                    <FollowBtn onClick={() => handleFollowToggle(user.user_alias, false)}>
                      {t.friends.followBack}
                    </FollowBtn>
                  )}
                    </FriendItem>
                  ))}
                </motion.div>
              )}

              {activeTab === 'followers' && followers.length === 0 && (
                <UserStats style={{ textAlign: 'center', padding: '20px' }}>
                  {t.friends.noFollowers}
                </UserStats>
              )}
            </FriendList>
          </ListSection>

        </PhoneBody>

        {/* ── 인라인 팝업 ── */}
        {popup && (
          <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(40,30,20,0.55)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', boxSizing: 'border-box'
          }}>
            <div style={{
              background: '#FDFBF4', borderRadius: '20px', padding: '28px 24px 20px',
              width: '90%', maxWidth: '340px', boxShadow: '0 16px 40px rgba(40,30,20,0.3)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', background: '#FFF0EA',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14, color: '#E07040'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
              </div>
              <p style={{ fontWeight: 700, fontSize: 17, color: '#3D3020', marginBottom: 8 }}>{popup.title}</p>
              <p style={{ fontSize: 14, color: '#6C5D47', lineHeight: 1.6, marginBottom: 22, whiteSpace: 'pre-line' }}>{popup.msg}</p>
              <button
                onClick={() => setPopup(null)}
                style={{
                  width: '100%', padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg,#E07040,#D05828)', color: '#fff',
                  fontSize: 15, fontWeight: 700, boxShadow: '0 4px 14px rgba(224,112,64,0.3)'
                }}
              >확인</button>
            </div>
          </div>
        )}
      </ResponsiveShell>
    </>
  );
};

export default FriendAdd;
